import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { cn } from "@/lib/utils";
import { textareaStyles } from "theme/components/textarea";
import { buttonStyles } from "theme/components/button";
import { API_CONFIG } from "@/constants/api";
import { ROUTES } from "@/constants/routes";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { customInstance } from "@/lib/axios-instance";
import { isAxiosError } from "axios";
import { ChevronLeft, Save } from "lucide-react";
import { useEffect, useState } from "react";
import * as React from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface JobOrderData {
  id: string;
  campaignName: string;
  name: string;
  approvalStatus: string;
  bgvRequirements: string;
  backfill: string;
  certifications: string;
  closedReason: string;
  confidential: boolean;
  contractType: string;
  dateFilled: string;
  datePosted: string;
  drugTestRequirement: string;
  earliestStartDateMrp: string;
  educations: string;
  employmentTypeJs: string;
  estimatedStartDate: string;
  goodToGoSkills: string;
  internalQuestionSet: string;
  jobUrl: string;
  jobAdvertisement: string;
  jobAssignmentLevel: string;
  jobLocationType: string;
  jobNumber: string;
  jobTitle: string;
  languagePreference: string;
  limitNumberOfSubmission: string;
  locationListNew: string;
  mandatorySkills: string;
  minExperience: string;
  negativeKeywords: string;
  passingScore: string;
  preferredCountryList: string;
  priority: string;
  radiusdistanceunit: string;
  radiusdistance: string;
  screeningRequirements: string;
  sendJobAlertToVmsa: boolean;
  status: string;
  talentLevel: string;
  term: string;
  totalYearsExperience: string;
  typeOfRequirement: string;
  jobsite: string;
  city: string;
  clientAddress: string;
  state: string;
  locationInfo: string; // JSON string
}

export default function EditJobOrderPage() {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_UPDATE">
      <EditJobOrderPageContent />
    </PageAccessGuard>
  );
}

function EditJobOrderPageContent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [formData, setFormData] = useState<JobOrderData>({
    id: "",
    campaignName: "",
    name: "",
    approvalStatus: "",
    bgvRequirements: "",
    backfill: "",
    certifications: "",
    closedReason: "",
    confidential: false,
    contractType: "",
    dateFilled: "",
    datePosted: "",
    drugTestRequirement: "",
    earliestStartDateMrp: "",
    educations: "",
    employmentTypeJs: "",
    estimatedStartDate: "",
    goodToGoSkills: "",
    internalQuestionSet: "",
    jobUrl: "",
    jobAdvertisement: "",
    jobAssignmentLevel: "",
    jobLocationType: "",
    jobNumber: "",
    jobTitle: "",
    languagePreference: "",
    limitNumberOfSubmission: "",
    locationListNew: "",
    mandatorySkills: "",
    minExperience: "",
    negativeKeywords: "",
    passingScore: "",
    preferredCountryList: "",
    priority: "",
    radiusdistanceunit: "",
    radiusdistance: "",
    screeningRequirements: "",
    sendJobAlertToVmsa: false,
    status: "",
    talentLevel: "",
    term: "",
    totalYearsExperience: "",
    typeOfRequirement: "",
    jobsite: "",
    city: "",
    clientAddress: "",
    state: "",
    locationInfo: ""
  });

  // Fetch job order data
  useEffect(() => {
    if (id) {
      fetchJobOrder();
    }
  }, [id]);

  const fetchJobOrder = async () => {
    try {
      setIsLoading(true);
      
      const response = await customInstance<any>({
        url: `${API_CONFIG.ENDPOINTS.TEMP_JOB_ORDER}/id/${id}`,
        method: 'GET'
      });

      const data = response.data;
      
      // Convert locationInfo object to JSON string for editing
      const locationInfoString = typeof data.locationInfo === 'object' 
        ? JSON.stringify(data.locationInfo, null, 2)
        : data.locationInfo || "";

      setFormData({
        ...data,
        locationInfo: locationInfoString
      });
    } catch (error) {
      console.error('Error fetching job order:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to load job order";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to load job order: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to load job order: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to load job order: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to load job order: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof JobOrderData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      
      // Parse locationInfo to ensure it's valid JSON
      let parsedLocationInfo;
      try {
        parsedLocationInfo = JSON.parse(formData.locationInfo);
      } catch (error) {
        toast.error("Invalid JSON in Location Information field");
        return;
      }

      const submitData = {
        ...formData,
        locationInfo: parsedLocationInfo
      };
      
      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.TEMP_JOB_ORDER}/id/${id}`,
        method: 'PUT',
        data: submitData
      });

      toast.success("Job order updated successfully!");
      navigate(ROUTES.TESTING_JOB_ORDER_MGMT);
    } catch (error) {
      console.error('Error updating job order:', error);
      
      // Extract error message from API response
      let errorMessage = "Failed to update job order";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to update job order: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to update job order: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to update job order: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to update job order: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-6">{UI_MESSAGES.LOADING}</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.TESTING_JOB_ORDER_MGMT)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Job Orders
        </Button>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Edit Job Order</h1>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className={cn(buttonStyles, "bg-blue-600 hover:bg-blue-700 text-white")}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Update Job Order"}
          </button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Job Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="id">ID</label>
                <input
                  id="id"
                  value={formData.id}
                  onChange={(e) => handleInputChange('id', e.target.value)}
                  disabled
                />
              </div>
              <div>
                <label htmlFor="campaignName">Campaign</label>
                <input
                  id="campaignName"
                  value={formData.campaignName}
                  onChange={(e) => handleInputChange('campaignName', e.target.value)}
                  disabled
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="jobTitle">Job Title</label>
                <input
                  id="jobTitle"
                  value={formData.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="jobNumber">Job Number</label>
                <input
                  id="jobNumber"
                  value={formData.jobNumber}
                  onChange={(e) => handleInputChange('jobNumber', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="status">Status</label>
                <input
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="priority">Priority</label>
                <input
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="employmentTypeJs">Employment Type</label>
                <input
                  id="employmentTypeJs"
                  value={formData.employmentTypeJs}
                  onChange={(e) => handleInputChange('employmentTypeJs', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="jobLocationType">Job Location Type</label>
                <input
                  id="jobLocationType"
                  value={formData.jobLocationType}
                  onChange={(e) => handleInputChange('jobLocationType', e.target.value)}
                />
              </div>

            <div>
              <label htmlFor="jobAdvertisement">Job Advertisement</label>
                <textarea
                id="jobAdvertisement"
                value={formData.jobAdvertisement}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('jobAdvertisement', e.target.value)}
                className={cn(textareaStyles, "min-h-[150px]")}
              />
            </div>

            <div>
              <label htmlFor="mandatorySkills">Mandatory Skills</label>
                <textarea
                id="mandatorySkills"
                value={formData.mandatorySkills}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('mandatorySkills', e.target.value)}
                className={cn(textareaStyles, "min-h-[100px]")}
              />
            </div>

              <div>
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="state">State</label>
                <input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
              </div>

            <div>
              <label htmlFor="locationInfo">Location Information (JSON)</label>
                <textarea
                id="locationInfo"
                value={formData.locationInfo}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('locationInfo', e.target.value)}
                className={cn(textareaStyles, "min-h-[200px] font-mono text-sm")}
                placeholder="Enter location information as JSON object..."
              />
            </div>

              <div>
                <label htmlFor="datePosted">Date Posted</label>
                <input
                  id="datePosted"
                  type="date"
                  value={formData.datePosted}
                  onChange={(e) => handleInputChange('datePosted', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="estimatedStartDate">Estimated Start Date</label>
                <input
                  id="estimatedStartDate"
                  type="date"
                  value={formData.estimatedStartDate}
                  onChange={(e) => handleInputChange('estimatedStartDate', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}